import React, { useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import toggleIcon from '../assets/expand.svg';
import '../styles/TableOfContents.css';

const styles = {
  toggleButton: (isVisible) => ({
    position: 'fixed',
    top: '20px',
    right: isVisible ? '370px' : '20px', // Dynamic positioning
    background: '#2a2a2a',
    borderRadius: '12px', // Square with slightly rounded corners
    width: '35px',
    height: '35px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.3), -4px -4px 8px rgba(50, 50, 50, 0.1)',
    cursor: 'pointer',
    zIndex: 1000,
    transition: 'all 0.3s ease',
    border: '1px solid rgba(255,255,255,0.1)',
  }),
  toggleIcon: (isVisible) => ({
    width: '20px',
    height: '20px',
    transform: isVisible ? 'rotate(180deg)' : 'rotate(0deg)', // Rotate icon on toggle
    transition: 'transform 0.3s ease',
  }),
  sidebar: (isVisible) => ({
    position: 'fixed',
    top: 0,
    right: isVisible ? 0 : '-350px', // Slide in/out
    width: '350px',
    height: '100vh',
    background: 'rgba(20, 20, 20, 0.7)',
    backdropFilter: 'blur(30px)',
    transition: 'right 0.3s ease',
    zIndex: 1000,
    overflowY: 'auto',
    boxShadow: '-4px 0 15px rgba(0,0,0,0.2)',
  }),
  sidebarContent: {
    padding: '20px',
  },
  sidebarTitle: {
    margin: '0 0 20px',
    fontFamily: "'Poppins', sans-serif",
    fontSize: '14px',
    fontWeight: '600',
    color: '#e0e0e0',
  },
  nodeList: {
    listStyle: 'none',
    paddingLeft: '12px',
    margin: 0,
  },
  nodeItem: {
    marginBottom: '4px',
  },
  nodeButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: 'none',
    border: 'none',
    color: '#b0b0b0',
    textAlign: 'left',
    fontFamily: "'Poppins', sans-serif",
    fontSize: '13px',
    cursor: 'pointer',
    width: '100%',
    padding: '6px 8px',
    borderRadius: '4px',
    transition: 'background 0.2s ease, color 0.2s ease',
  },
  expandButton: {
    background: 'none',
    border: 'none',
    color: '#888',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '4px',
  },
};

const TableOfContents = ({ nodes, onNodeClick, isMobile, isSearchTriggered }) => {
  const [isVisible, setIsVisible] = useState(isSearchTriggered ? true : false);

  useEffect(() => {
    setIsVisible(isSearchTriggered ? true : false);
  }, [isSearchTriggered]);
  const [highlightedNode, setHighlightedNode] = useState(null); // New state for highlighted node

  // Create hierarchical structure first
  const hierarchicalNodes = useMemo(() => {
    const map = {};
    const roots = [];

    nodes.forEach((node) => {
      map[node.id] = { ...node, children: [] };
    });

    nodes.forEach((node) => {
      if (node.parentNode) {
        map[node.parentNode].children.push(map[node.id]);
      } else {
        roots.push(map[node.id]);
      }
    });

    return roots;
  }, [nodes]);

  // Initialize expandedNodes state based on the hierarchical structure
  const [expandedNodes, setExpandedNodes] = useState({});

  // This effect runs once on mount and whenever nodes change
  // It sets all nodes to be expanded by default
  useEffect(() => {
    const expanded = {};

    // Function to recursively set all nodes to expanded
    const expandAllNodes = (nodeList) => {
      nodeList.forEach(node => {
        expanded[node.id] = true;
        if (node.children && node.children.length > 0) {
          expandAllNodes(node.children);
        }
      });
    };

    expandAllNodes(hierarchicalNodes);
    setExpandedNodes(expanded);
  }, [hierarchicalNodes]);

  useEffect(() => {
    const exportButton = document.querySelector('.export-button');
    if (exportButton) {
      if (isVisible) {
        exportButton.style.right = '370px'; // Same as sidebar width
      } else {
        exportButton.style.right = '20px';
      }
    }
  }, [isVisible]);

  const toggleNodeExpand = useCallback((nodeId) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  }, []);

  const toggleSidebar = () => {
    setIsVisible((prev) => !prev);
  };

  const renderNodes = (nodeList) => (
    <ul style={styles.nodeList}>
      {nodeList.map((node) => {
        const hasChildren = node.children && node.children.length > 0;
        const isNodeExpanded = expandedNodes[node.id];
        const isHighlighted = highlightedNode === node.id;

        return (
          <li key={node.id} style={styles.nodeItem}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
              }}
              onMouseEnter={() => setHighlightedNode(node.id)}
              onMouseLeave={() => setHighlightedNode(null)}
            >
              {hasChildren && (
                <button
                  onClick={() => toggleNodeExpand(node.id)}
                  style={styles.expandButton}
                  aria-label={`${isNodeExpanded ? 'Collapse' : 'Expand'} ${node.data.originalHeader}`}
                >
                  {isNodeExpanded ? '▼' : '▶'}
                </button>
              )}
              <button
                onClick={() => {
                  onNodeClick(node.id);
                  setHighlightedNode(node.id);
                }}
                className="node-button"
                style={{
                  ...(isHighlighted ? { color: '#e0e0e0', background: 'rgba(255,255,255,0.1)' } : {}),
                }}
                aria-label={`Navigate to ${node.data.originalHeader}`}
              >
                {node.data.originalHeader || 'Unnamed Node'}
              </button>
            </div>
            {hasChildren && isNodeExpanded && renderNodes(node.children)}
          </li>
        );
      })}
    </ul>
  );

  if (isMobile) {
    return null;
  }

  return (
    <>
      {/* Toggle Button */}
      <div
        style={styles.toggleButton(isVisible)}
        onClick={toggleSidebar}
        aria-label={`${isVisible ? 'Hide' : 'Show'} Outline`}
      >
        <img
          src={toggleIcon}
          alt="Toggle Sidebar"
          style={styles.toggleIcon(isVisible)}
        />
      </div>

      {/* Sidebar */}
      <div style={styles.sidebar(isVisible)}>
        <div style={styles.sidebarContent}>
          <h3 style={styles.sidebarTitle}>Outline</h3>
          {renderNodes(hierarchicalNodes)}
        </div>
      </div>
    </>
  );
};

TableOfContents.propTypes = {
  nodes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      parentNode: PropTypes.string,
      data: PropTypes.shape({
        originalHeader: PropTypes.string,
      }),
    })
  ).isRequired,
  onNodeClick: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired, // New Prop
};

export default TableOfContents;